import { Component, type ErrorInfo, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  className?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          className={cn(
            'mx-auto max-w-md rounded-lg border border-border bg-background p-8 text-center text-foreground',
            this.props.className
          )}
        >
          <h2 className="font-display text-2xl text-balance mb-2">Une erreur est survenue</h2>
          <p className="text-sm text-pretty text-muted-foreground mb-4">
            Désolée pour le contretemps. Vous pouvez recharger la page pour réessayer.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-primary-foreground text-sm font-bold uppercase tracking-widest hover:bg-primary/90"
          >
            Recharger
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
